/**
 * PlanningDashboard — workspace-level aggregated Planning dashboard.
 * Lets the user pick one-or-many fiscal years, and for each FY either
 * include "all budgets" or a single specific budget. Renders 4 charts:
 *   1) Revenue vs Expense line across all periods
 *   2) Net cash flow line
 *   3) Stacked area of top manual items
 *   4) Per-budget bar chart of net contribution
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar, XAxis, YAxis,
  Tooltip, Legend, ResponsiveContainer, CartesianGrid, ReferenceLine, ComposedChart,
} from "recharts";
import { TrendingUp, TrendingDown, Sparkles, RefreshCw, LayoutGrid, GitCompare } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { fmtMoney } from "@/lib/format";

// Deterministic palette — rotated across up to 10 items.
const PALETTE = ["#0d9488", "#f97316", "#8b5cf6", "#3b82f6", "#eab308",
                        "#ef4444", "#10b981", "#e11d48", "#06b6d4", "#a855f7"];

const SOURCES = [
  { v: "quotations", label: "Quotations (pipeline)" },
  { v: "so", label: "Sales orders (committed)" },
  { v: "manual", label: "Manual items" },
  { v: "invoiced", label: "Invoiced (accrual actual)" },
  { v: "collected", label: "Collected (cash actual)" },
  { v: "bills", label: "Bills" },
  { v: "payroll", label: "Payroll" },
  { v: "jl", label: "Journals" },
];

export default function PlanningDashboard({ wsId }) {
  const [idx, setIdx] = useState(null);        // { fiscal_years, budgets_by_fy }
  const [selectedFys, setSelectedFys] = useState([]);
  // Map<fy, "all" | budgetId>
  const [fyBudgetChoice, setFyBudgetChoice] = useState({});
  const [enabledSources, setEnabledSources] = useState(() => new Set(["manual", "so", "quotations", "bills", "payroll", "invoiced", "collected"]));
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showVariance, setShowVariance] = useState(false);
  const [showBand, setShowBand] = useState(true);
  const [bandPct, setBandPct] = useState(15);

  // Load budget/FY index
  useEffect(() => {
    if (!wsId) return;
    (async () => {
      try {
        const { data } = await api.get(`/workspaces/${wsId}/planning-budgets`);
        setIdx(data);
        // Default to the 2 most recent FYs with "all" selection
        const top = (data.fiscal_years || []).slice(0, 2);
        setSelectedFys(top);
        const map = {}; top.forEach((f) => { map[f] = "all"; });
        setFyBudgetChoice(map);
      } catch (e) { toast.error(apiErrorMessage(e, "Failed to load budgets")); }
    })();
  }, [wsId]);

  // Compute budget_ids param from choices
  const budgetIdsParam = useMemo(() => {
    const ids = [];
    selectedFys.forEach((f) => {
      const c = fyBudgetChoice[f];
      if (c && c !== "all") ids.push(c);
      else if (c === "all") {
        (idx?.budgets_by_fy?.[f] || []).forEach((b) => ids.push(b.id));
      }
    });
    return Array.from(new Set(ids));
  }, [selectedFys, fyBudgetChoice, idx]);

  const load = async () => {
    if (!wsId || selectedFys.length === 0) return;
    setBusy(true);
    try {
      const params = new URLSearchParams();
      params.set("fy", selectedFys.join(","));
      if (budgetIdsParam.length) params.set("budget_ids", budgetIdsParam.join(","));
      params.set("sources", Array.from(enabledSources).join(","));
      const endpoint = showVariance ? "planning-variance" : "planning";
      const { data } = await api.get(`/workspaces/${wsId}/${endpoint}?${params}`);
      setPlan(data);
    } catch (e) { toast.error(apiErrorMessage(e, "Planning failed")); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, [wsId, selectedFys, budgetIdsParam, enabledSources, showVariance]); // eslint-disable-line

  const toggleFy = (fy) => {
    setSelectedFys((prev) => {
      if (prev.includes(fy)) return prev.filter((x) => x !== fy);
      return [...prev, fy];
    });
    setFyBudgetChoice((prev) => ({ ...prev, [fy]: prev[fy] || "all" }));
  };
  const toggleSource = (s) => {
    setEnabledSources((prev) => {
      const n = new Set(prev);
      if (n.has(s)) n.delete(s); else n.add(s);
      return n;
    });
  };

  // Derive chart-friendly rows
  const chartRows = useMemo(() => {
    const factor = (bandPct || 0) / 100;
    return (plan?.rows || []).map((r) => ({
      period: r.period,
      revenue: r.revenue.total,
      expense: r.expense.total,
      net: r.net,
      // Confidence band bounds — recharts renders these as an Area between low/high.
      revenue_low: Math.round(r.revenue.total * (1 - factor)),
      revenue_high: Math.round(r.revenue.total * (1 + factor)),
      expense_low: Math.round(r.expense.total * (1 - factor)),
      expense_high: Math.round(r.expense.total * (1 + factor)),
    }));
  }, [plan, bandPct]);

  // Cumulative net cash-flow
  const cashflow = useMemo(() => {
    let acc = 0;
    return (plan?.rows || []).map((r) => {
      acc += r.net;
      return { period: r.period, cumulative_net: Math.round(acc * 100) / 100, period_net: r.net };
    });
  }, [plan]);

  // Stacked-area data for top manual items
  const stackedData = useMemo(() => {
    if (!plan?.rows || !plan?.top_manual_items?.length) return { data: [], keys: [] };
    // Fetch per-item per-period contributions by re-computing recurrence client-side
    const items = plan.top_manual_items;
    const rows = plan.periods.map((p) => {
      const row = { period: p };
      items.forEach((it) => {
        row[it.label] = _hits(it, p) * (it.kind === "expense" ? -1 : 1);
      });
      return row;
    });
    return { data: rows, keys: items.map((it) => it.label) };
  }, [plan]);

  const byBudget = useMemo(() => (plan?.by_budget || []).map((b) => ({
    name: b.budget_name || b.budget_id.slice(0, 6),
    revenue: b.revenue_total,
    expense: b.expense_total,
    net: b.net,
    fy: b.fiscal_year,
  })), [plan]);

  const money = (v) => fmtMoney(v, "INR");

  if (!idx) return <div className="text-sm text-muted-foreground">Loading planning index…</div>;
  const hasAny = (idx.fiscal_years || []).length > 0;

  return (
    <div className="space-y-4" data-testid="planning-dashboard">
      {/* Controls */}
      <div className="border border-border bg-muted/30 p-3 space-y-2" data-testid="planning-controls">
        <div className="flex items-center gap-2 flex-wrap">
          <LayoutGrid className="w-4 h-4 text-primary" />
          <Label className="label-eyebrow text-[10px]">Fiscal years</Label>
          {!hasAny && <span className="text-xs text-muted-foreground italic">No budgets yet — create budgets under the Budgets tab.</span>}
          {idx.fiscal_years.map((f) => (
            <label key={f} className={`chip text-xs cursor-pointer ${selectedFys.includes(f) ? "chip-approved" : ""}`}>
              <input type="checkbox" checked={selectedFys.includes(f)} onChange={() => toggleFy(f)} className="mr-1" data-testid={`planning-fy-${f}`} />
              {f}
            </label>
          ))}
          <Button size="sm" variant="ghost" className="rounded-none h-7 ml-auto" onClick={load} disabled={busy} data-testid="planning-refresh">
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${busy ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>

        {selectedFys.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            <Label className="label-eyebrow text-[10px]">Budget per FY</Label>
            {selectedFys.map((f) => {
              const opts = idx.budgets_by_fy?.[f] || [];
              return (
                <div key={f} className="flex items-center gap-1">
                  <span className="chip text-[10px]">{f}</span>
                  <Select value={fyBudgetChoice[f] || "all"} onValueChange={(v) => setFyBudgetChoice((prev) => ({ ...prev, [f]: v }))}>
                    <SelectTrigger className="h-7 min-w-[180px] rounded-none text-xs" data-testid={`planning-fy-budget-${f}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All budgets ({opts.length})</SelectItem>
                      {opts.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              );
            })}
          </div>
        )}

        <div className="flex items-center gap-2 flex-wrap">
          <Label className="label-eyebrow text-[10px]">Sources</Label>
          {SOURCES.map((s) => (
            <label key={s.v} className="text-xs inline-flex items-center gap-1 cursor-pointer">
              <input type="checkbox" checked={enabledSources.has(s.v)} onChange={() => toggleSource(s.v)} data-testid={`planning-source-${s.v}`} />
              <span className="chip text-[10px]">{s.label}</span>
            </label>
          ))}
          <div className="ml-auto flex items-center gap-2 flex-wrap">
            <label className="text-xs inline-flex items-center gap-1 cursor-pointer">
              <input type="checkbox" checked={showBand} onChange={(e) => setShowBand(e.target.checked)} data-testid="planning-band-toggle" />
              <span className="chip text-[10px]">Confidence ±</span>
            </label>
            <input
              type="number" min={0} max={50} value={bandPct}
              onChange={(e) => setBandPct(Number(e.target.value) || 0)}
              className="h-7 w-16 text-xs rounded-none border border-border px-2"
              data-testid="planning-band-pct"
            />
            <span className="text-[10px] text-muted-foreground">%</span>
            <label className="text-xs inline-flex items-center gap-1 cursor-pointer border-l border-border pl-2 ml-1">
              <input type="checkbox" checked={showVariance} onChange={(e) => setShowVariance(e.target.checked)} data-testid="planning-variance-toggle" />
              <GitCompare className="w-3 h-3" />
              <span className="chip text-[10px]">Planned vs Actual</span>
            </label>
          </div>
        </div>
      </div>

      {/* Grand summary */}
      {plan && (
        <>
        <div className="grid grid-cols-4 gap-3" data-testid="planning-grand-summary">
          <SumTile label="Projected revenue" value={plan.summary.total_revenue} icon={TrendingUp} tone="ok" money={money} testid="planning-grand-rev" />
          <SumTile label="Projected expense" value={plan.summary.total_expense} icon={TrendingDown} tone="danger" money={money} testid="planning-grand-exp" />
          <SumTile label="Net" value={plan.summary.net} icon={Sparkles} tone={plan.summary.net >= 0 ? "ok" : "danger"} money={money} testid="planning-grand-net" />
          <div className="border border-border p-3" data-testid="planning-grand-count">
            <div className="label-eyebrow text-[10px]">Budgets aggregated</div>
            <div className="text-2xl font-black font-mono">{plan.summary.budget_count ?? 0}</div>
            <div className="text-[10px] text-muted-foreground">across {selectedFys.length} FY{selectedFys.length !== 1 ? "s" : ""}</div>
          </div>
        </div>
        {/* Pipeline / Accrual / Cash actuals breakdown */}
        {(() => {
          const rows = plan.rows || [];
          const sumRev = (k) => rows.reduce((acc, r) => acc + Number(r?.revenue?.[k] || 0), 0);
          const pipe = sumRev("quotations");
          const so = sumRev("so");
          const invoiced = sumRev("invoiced");
          const collected = sumRev("collected");
          return (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="planning-actuals-summary">
              <SumTile label="Pipeline (Quotations · weighted)" value={pipe} icon={Sparkles} tone="neutral" money={money} testid="planning-pipeline" />
              <SumTile label="Committed (Sales orders)" value={so} icon={TrendingUp} tone="neutral" money={money} testid="planning-committed" />
              <SumTile label="Invoiced (accrual actual)" value={invoiced} icon={TrendingUp} tone="ok" money={money} testid="planning-invoiced" />
              <SumTile label="Collected (cash actual)" value={collected} icon={TrendingUp} tone="ok" money={money} testid="planning-collected" />
            </div>
          );
        })()}
        </>
      )}

      {/* CHARTS */}
      {plan && chartRows.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <ChartCard title="Revenue vs Expense (multi-year)" testKey="rev-exp">
            <ResponsiveContainer width="100%" height={280}>
              <ComposedChart data={chartRows}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.35} />
                <XAxis dataKey="period" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => compact(v)} />
                <Tooltip formatter={(v) => money(v)} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {showBand && bandPct > 0 && (
                  <>
                    <Area type="monotone" dataKey="revenue_low" stackId="revBand"
                              stroke="none" fill="transparent" legendType="none" />
                    <Area type="monotone" name={`Revenue ±${bandPct}%`}
                              dataKey={(d) => Math.max(0, d.revenue_high - d.revenue_low)}
                              stackId="revBand" stroke="none" fill="#10b981" fillOpacity={0.15} />
                    <Area type="monotone" dataKey="expense_low" stackId="expBand"
                              stroke="none" fill="transparent" legendType="none" />
                    <Area type="monotone" name={`Expense ±${bandPct}%`}
                              dataKey={(d) => Math.max(0, d.expense_high - d.expense_low)}
                              stackId="expBand" stroke="none" fill="#ef4444" fillOpacity={0.13} />
                  </>
                )}
                <Line type="monotone" dataKey="revenue" name="Revenue" stroke="#10b981" strokeWidth={2} dot={{ r: 2 }} />
                <Line type="monotone" dataKey="expense" name="Expense" stroke="#ef4444" strokeWidth={2} dot={{ r: 2 }} />
                <ReferenceLine y={0} stroke="#666" strokeOpacity={0.3} />
              </ComposedChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Cumulative net cash flow" testKey="cashflow">
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={cashflow}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.35} />
                <XAxis dataKey="period" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => compact(v)} />
                <Tooltip formatter={(v) => money(v)} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="cumulative_net" name="Cumulative net" stroke="#3b82f6" strokeWidth={2.5} dot={{ r: 2 }} />
                <Line type="monotone" dataKey="period_net" name="Period net" stroke="#a855f7" strokeWidth={1.5} dot={false} opacity={0.5} />
                <ReferenceLine y={0} stroke="#666" strokeOpacity={0.5} />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Top manual items — stacked contribution" testKey="stacked">
            {stackedData.data.length === 0 || stackedData.keys.length === 0 ? (
              <div className="text-xs text-muted-foreground p-8 text-center">No manual items yet — add them via the per-budget Planning dialog.</div>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={stackedData.data}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.35} />
                  <XAxis dataKey="period" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => compact(v)} />
                  <Tooltip formatter={(v) => money(v)} />
                  <Legend wrapperStyle={{ fontSize: 10 }} />
                  {stackedData.keys.map((k, i) => (
                    <Area key={k} type="monotone" dataKey={k} stackId="1"
                              stroke={PALETTE[i % PALETTE.length]}
                              fill={PALETTE[i % PALETTE.length]} fillOpacity={0.55} />
                  ))}
                  <ReferenceLine y={0} stroke="#666" strokeOpacity={0.5} />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </ChartCard>

          <ChartCard title="Per-budget breakdown" testKey="by-budget">
            {byBudget.length === 0 ? (
              <div className="text-xs text-muted-foreground p-8 text-center">Nothing to break down.</div>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={byBudget} margin={{ top: 6, right: 10, left: 0, bottom: 6 }}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.35} />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-12} textAnchor="end" height={50} />
                  <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => compact(v)} />
                  <Tooltip formatter={(v) => money(v)} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="revenue" name="Revenue" fill="#10b981" />
                  <Bar dataKey="expense" name="Expense" fill="#ef4444" />
                  <Bar dataKey="net" name="Net" fill="#3b82f6" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </div>
      )}

      {/* Detail: Top manual items list */}
      {plan?.top_manual_items?.length > 0 && (
        <div className="border border-border" data-testid="planning-top-items">
          <div className="px-3 py-2 bg-muted/30 border-b border-border label-eyebrow text-[10px]">Top manual items (by absolute impact)</div>
          {plan.top_manual_items.map((it, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 px-3 py-2 border-b border-border last:border-0 items-center text-sm">
              <span className="col-span-1"><span className={`chip text-[10px] ${it.kind === "revenue" ? "chip-approved" : "chip-rejected"}`}>{it.kind}</span></span>
              <span className="col-span-4 truncate font-medium">{it.label}</span>
              <span className="col-span-3 text-xs text-muted-foreground truncate">{it.budget_name}</span>
              <span className="col-span-1 chip text-[10px] text-center">{it.recurrence}</span>
              <span className="col-span-3 text-right font-mono">{money(it.impact)}</span>
            </div>
          ))}
        </div>
      )}

      {/* Variance panel */}
      {showVariance && plan?.variance?.length > 0 && (
        <VariancePanel variance={plan.variance} summary={plan.summary_variance} money={money} />
      )}

      {busy && <div className="text-xs text-muted-foreground">Refreshing…</div>}
    </div>
  );
}

function VariancePanel({ variance, summary, money }) {
  const chartData = variance.map((v) => ({
    period: v.period,
    projected_revenue: v.projected_revenue,
    actual_revenue: v.actual_revenue,
    projected_expense: v.projected_expense,
    actual_expense: v.actual_expense,
  }));
  return (
    <div className="border-2 border-primary/50 space-y-3 p-3" data-testid="planning-variance-panel">
      <div className="flex items-center gap-2">
        <GitCompare className="w-4 h-4 text-primary" />
        <span className="text-sm font-bold">Planned vs Actual (from posted journals)</span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <VarianceTile label="Projected revenue" value={summary.projected_revenue} money={money} tone="ok" />
        <VarianceTile label="Actual revenue" value={summary.actual_revenue} money={money} tone="ok" drift={summary.revenue_drift_pct} />
        <VarianceTile label="Projected expense" value={summary.projected_expense} money={money} tone="danger" />
        <VarianceTile label="Actual expense" value={summary.actual_expense} money={money} tone="danger" drift={summary.expense_drift_pct} />
      </div>
      <div className="border border-border">
        <ResponsiveContainer width="100%" height={260}>
          <ComposedChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.35} />
            <XAxis dataKey="period" tick={{ fontSize: 10 }} />
            <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => compact(v)} />
            <Tooltip formatter={(v) => money(v)} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Line type="monotone" dataKey="projected_revenue" name="Projected revenue" stroke="#10b981" strokeWidth={2} strokeDasharray="4 3" dot={false} />
            <Line type="monotone" dataKey="actual_revenue" name="Actual revenue" stroke="#065f46" strokeWidth={2.5} dot={{ r: 2 }} />
            <Line type="monotone" dataKey="projected_expense" name="Projected expense" stroke="#ef4444" strokeWidth={2} strokeDasharray="4 3" dot={false} />
            <Line type="monotone" dataKey="actual_expense" name="Actual expense" stroke="#991b1b" strokeWidth={2.5} dot={{ r: 2 }} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <div className="border border-border overflow-x-auto">
        <div className="grid grid-cols-9 gap-1 px-3 py-2 bg-muted/40 label-eyebrow text-[10px] min-w-[720px]">
          <div>Period</div>
          <div className="text-right">Proj. rev</div>
          <div className="text-right">Actual rev</div>
          <div className="text-right">Drift</div>
          <div className="text-right">Proj. exp</div>
          <div className="text-right">Actual exp</div>
          <div className="text-right">Drift</div>
          <div className="text-right">Proj. net</div>
          <div className="text-right">Actual net</div>
        </div>
        {variance.map((v) => (
          <div key={v.period} className="grid grid-cols-9 gap-1 px-3 py-1.5 border-t border-border text-xs items-center font-mono min-w-[720px]" data-testid={`variance-row-${v.period}`}>
            <div>{v.period}</div>
            <div className="text-right">{money(v.projected_revenue)}</div>
            <div className="text-right">{money(v.actual_revenue)}</div>
            <div className="text-right"><DriftChip pct={v.revenue_drift_pct} favor="up" /></div>
            <div className="text-right">{money(v.projected_expense)}</div>
            <div className="text-right">{money(v.actual_expense)}</div>
            <div className="text-right"><DriftChip pct={v.expense_drift_pct} favor="down" /></div>
            <div className={`text-right ${v.projected_net >= 0 ? "text-emerald-600" : "text-red-600"}`}>{money(v.projected_net)}</div>
            <div className={`text-right font-bold ${v.actual_net >= 0 ? "text-emerald-700" : "text-red-700"}`}>{money(v.actual_net)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function VarianceTile({ label, value, money, tone = "ok", drift }) {
  const c = tone === "danger" ? "text-red-600" : "text-emerald-600";
  return (
    <div className="border border-border p-2 bg-muted/20">
      <div className="label-eyebrow text-[10px]">{label}</div>
      <div className={`text-lg font-black font-mono ${c}`}>{money(value)}</div>
      {drift != null && <DriftChip pct={drift} favor={tone === "danger" ? "down" : "up"} />}
    </div>
  );
}

function DriftChip({ pct, favor }) {
  if (pct == null) return <span className="text-[10px] text-muted-foreground">n/a</span>;
  const good = (favor === "up" && pct >= 0) || (favor === "down" && pct <= 0);
  const color = good ? "text-emerald-600" : "text-red-600";
  return (
    <span className={`text-[10px] font-mono ${color}`} title="Actual vs Projected drift">
      {pct >= 0 ? "+" : ""}{pct}%
    </span>
  );
}

function SumTile({ label, value, icon: Icon, tone, testid, money }) {
  const c = tone === "danger" ? "text-red-600"
    : tone === "neutral" ? "text-blue-600"
    : "text-emerald-600";
  return (
    <div className="border border-border p-3" data-testid={testid}>
      <div className="flex items-center gap-1 label-eyebrow text-[10px]"><Icon className={`w-3 h-3 ${c}`} /> {label}</div>
      <div className={`text-2xl font-black font-mono ${c}`}>{money(value)}</div>
    </div>
  );
}

function ChartCard({ title, testKey, children }) {
  return (
    <div className="border border-border" data-testid={`planning-chart-${testKey}`}>
      <div className="px-3 py-2 bg-muted/30 border-b border-border label-eyebrow text-[10px]">{title}</div>
      <div className="p-2">{children}</div>
    </div>
  );
}

function compact(v) {
  const n = Number(v) || 0;
  const a = Math.abs(n);
  if (a >= 10_000_000) return `${(n / 10_000_000).toFixed(1)}Cr`;
  if (a >= 100_000) return `${(n / 100_000).toFixed(1)}L`;
  if (a >= 1000) return `${(n / 1000).toFixed(0)}K`;
  return `${n}`;
}

// Client-side mirror of the backend `_recurring_hits`
function _hits(item, period) {
  const start = item.start_period || "";
  const end = item.end_period;
  if (period < start) return 0;
  if (end && period > end) return 0;
  const amt = Number(item.amount) || 0;
  const rec = item.recurrence || "one_time";
  if (rec === "one_time") return period === start ? amt : 0;
  if (rec === "monthly") return amt;
  if (rec === "quarterly") {
    const [sy, sm] = start.split("-").map(Number);
    const [py, pm] = period.split("-").map(Number);
    const delta = (py - sy) * 12 + (pm - sm);
    return delta >= 0 && delta % 3 === 0 ? amt : 0;
  }
  if (rec === "yearly") {
    const [sy, sm] = start.split("-").map(Number);
    const [py, pm] = period.split("-").map(Number);
    return pm === sm && py >= sy ? amt : 0;
  }
  return 0;
}
